import React, { useEffect, useRef, useState } from "react";
import { addToLibrary } from '../marketing-v2/services/imageLibrary';
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Quill from "quill";
import SlidePanel from "../shared/SlidePanel";
import toast from "../shared/Toast";
import siteSetting from "../../config/env";
import { marketingAction } from "../../_actions/marketing.actions";
import { ChannelToggle, SmsPreview, marketingSelectProps, EmailFrame } from "./MarketingEditorParts";
import "./CustomReminderPanel.css";
import { CampaignComposer } from "../marketing-v2/features/email-composer";
import { BlockRenderer } from "../marketing-v2/features/email-composer/components/BlockRenderer";
import { blocksToFallbackHtml } from "../marketing-v2/features/email-composer/data/blocksToHtml";
import { EmailBlocksPreview } from "../marketing-v2/features/email-composer/components/EmailHtml";
import { renderEmail, isThemedEmail } from "../marketing-v2/features/email-composer/email/render";
import { salonLinks, resolveLocalLinks } from "../marketing-v2/features/email-composer/email/links";

// ── Email-safe font registration for Quill (run once) ──
if (!document.getElementById("crp-quill-fonts")) {
  const fontCSS = `
    .ql-font-helvetica { font-family: Helvetica, Arial, sans-serif; }
    .ql-font-arial { font-family: Arial, sans-serif; }
    .ql-font-times-new-roman { font-family: 'Times New Roman', Times, serif; }
    .ql-font-georgia { font-family: Georgia, serif; }
    .ql-font-courier-new { font-family: 'Courier New', Courier, monospace; }
    .ql-font-verdana { font-family: Verdana, Geneva, sans-serif; }
    .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="helvetica"]::before,
    .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="helvetica"]::before { content: "Helvetica"; font-family: Helvetica, Arial, sans-serif; }
    .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="arial"]::before,
    .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="arial"]::before { content: "Arial"; font-family: Arial, sans-serif; }
    .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="times-new-roman"]::before,
    .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="times-new-roman"]::before { content: "Times New Roman"; font-family: 'Times New Roman', Times, serif; }
    .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="georgia"]::before,
    .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="georgia"]::before { content: "Georgia"; font-family: Georgia, serif; }
    .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="courier-new"]::before,
    .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="courier-new"]::before { content: "Courier New"; font-family: 'Courier New', Courier, monospace; }
    .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="verdana"]::before,
    .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="verdana"]::before { content: "Verdana"; font-family: Verdana, Geneva, sans-serif; }
  `;
  const styleEl = document.createElement("style");
  styleEl.id = "crp-quill-fonts";
  styleEl.textContent = fontCSS;
  document.head.appendChild(styleEl);
  const Font = Quill.import("formats/font");
  Font.whitelist = ["helvetica", "arial", "times-new-roman", "georgia", "courier-new", "verdana"];
  Quill.register(Font, true);
}

const quillModules = {
  toolbar: [
    [{ font: ["helvetica", "arial", "times-new-roman", "georgia", "courier-new", "verdana"] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    ["blockquote", "code-block"],
    ["link"],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    ["image"],
    ["clean"],
  ],
};

const MERGE_TAGS = [
  { cls: "bg_1", label: "Salon Name", value: "{{SalonName}}" },
  { cls: "bg_2", label: "Client's Name", value: "{{Client}}" },
  { cls: "bg_3", label: "Stylist's Name", value: "{{StylistName}}" },
  { cls: "bg_4", label: "Next Appointment Date", value: "{{DateTime}}" },
];

function MergeTags({ onInsert }) {
  return (
    <div className="editable_span_prent mb-2">
      {MERGE_TAGS.map((t) => (
        <span key={t.value} className={`editableContentButton ${t.cls}`} onClick={() => onInsert(t.value)}>
          {t.label}
        </span>
      ))}
    </div>
  );
}

// Quill inserts <img> with no size constraint, so wide images overflow the email.
// Add an inline max-width to any image that doesn't already have a style attribute.
function constrainImages(html) {
  if (typeof html !== "string") return html;
  return html.replace(/<img(?![^>]*\bstyle=)/gi, '<img style="max-width:100%;height:auto;display:block;" ');
}

function generateCustomEmailHTML({ previewText, title, headerImage, footerText }) {
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"><title>${title || "Custom Reminder"}</title>
  <style>body{font-family:Arial,sans-serif;line-height:1.6;color:#333;max-width:600px;margin:0 auto;padding:20px}.email-container{border:1px solid #e0e0e0;border-radius:5px;overflow:hidden}.header{background-color:#f8f9fa;padding:20px;text-align:center}.header img{width:100%;max-width:600px;height:auto;display:block;margin:0 auto}.content{padding:20px;background-color:#fff}.footer{background-color:#f8f9fa;padding:15px;text-align:center;font-size:12px;color:#666;border-top:1px solid #e0e0e0}.content img,.footer img{max-width:100%;height:auto;display:block;margin:0 auto}</style></head>
  <body><div class="email-container">${headerImage ? `<div class="header"><img src="${headerImage}" alt="Email Header" style="width:100%;max-width:600px;height:auto;display:block;margin:0 auto"/></div>` : ""}<div class="content">${previewText || "No content provided"}</div>${footerText ? `<div class="footer">${footerText}</div>` : ""}</div></body></html>`;
}

// ── Stacked custom email preview (renders the composed HTML) ──
function CustomEmailPreviewPanel({ html, onClose }) {
  return (
    <SlidePanel title="Email Preview" onClose={onClose} width={640} zIndex={100010}>
      <EmailFrame html={html} />
    </SlidePanel>
  );
}

function CustomReminderPanel({ data, salonId, isCreate, onClose, onSaved }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state?.user);

  const triggerTypeOptions = siteSetting?.triggerTypeOptions;
  const reminderTimeTypeOption = siteSetting?.reminderTimeOptions;
  const defaultReminderText =
    siteSetting?.SMSText?.customReminderText ||
    "Hi {{Client}}, this is {{SalonName}}. Just a reminder for your appointment with {{StylistName}} at {{DateTime}}";

  const [mode, setMode] = useState(isCreate ? "edit" : "view"); // 'view' | 'edit'
  const [showMenu, setShowMenu] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [activeSection, setActiveSection] = useState("details");
  const [activeTab, setActiveTab] = useState("email"); // drives content validation
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [smsInlineOpen, setSmsInlineOpen] = useState(false);

  const [form, setForm] = useState(() => {
    const _trigger = triggerTypeOptions.find((t) => t?.value === data?.triggerType);
    const _timeType = reminderTimeTypeOption.find((t) => t?.value === data?.reminderTimeType);
    return {
      title: data?.title || "",
      description: data?.description || "",
      emailText: data?.emailText || data?.automationText || "",
      smsText: data?.smsText || "",
      emailSubject: data?.emailSubject || "",
      triggerType: _trigger || null,
      reminderTime: data?.reminderTime != null ? String(data.reminderTime) : "",
      reminderTimeType: _timeType || null,
      statusSMSreminder: data?.statusSMSreminder || 0,
      statusEmailReminder: data?.statusEmailReminder || 0,
    };
  });
  const [footerText, setFooterText] = useState(data?.footerText || "");
  // Designed emails: blocks built in the email designer. 'blocks' sends the
  // design (with a generated text fallback until the new engine is live);
  // 'text' is the original simple editor.
  const [emailBlocks, setEmailBlocks] = useState(() => (Array.isArray(data?.emailBlocks) ? data.emailBlocks : []));
  // New automations start in designed mode; existing ones keep whatever
  // format they were saved with (legacy items have only text)
  const [emailFormat, setEmailFormat] = useState(
    data?.emailFormat || (Array.isArray(data?.emailBlocks) && data.emailBlocks.length ? "blocks" : (isCreate ? "blocks" : "text"))
  );
  const [showDesigner, setShowDesigner] = useState(false);
  const [salonImage, setSalonImage] = useState(data?.headerImage || null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [emailPreviewHtml, setEmailPreviewHtml] = useState(null);

  const editContent = useRef();
  const emailSubjectRef = useRef();
  const smsTextRef = useRef();
  const footerTextRef = useRef();
  const fileInputRef = useRef(null);

  const clearError = (field) => setErrors((e) => ({ ...e, [field]: "" }));
  const setField = (field, value) => {
    clearError(field);
    setForm((f) => ({ ...f, [field]: value }));
  };

  const goSection = (section) => {
    setActiveSection(section);
    if (section === "email" || section === "sms") setActiveTab(section);
  };

  // ── Image upload ──
  const imageHandler = () => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/png, image/jpeg, image/gif");
    input.click();
    input.onchange = async () => {
      const file = input.files[0];
      if (!["image/jpeg", "image/png", "image/gif"].includes(file.type)) {
        toast.error("Only JPG, PNG and GIF images are allowed");
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Image size should be less than 2MB");
        return;
      }
      try {
        const response = await dispatch(marketingAction.uploadEmailBodyImage(file));
        if (response?.data?.statusCode === 1) {
          const uploaded = response.data.responseData;
          const imageUrl = uploaded.imageUrl;
          const quill = editContent.current.getEditor();
          const range = quill.getSelection();
          quill.insertEmbed(range ? range.index : quill.getLength(), "image", imageUrl);
          setUploadedImages((prev) => [...prev, imageUrl]);
          addToLibrary({ id: uploaded.id, url: imageUrl, name: file.name, width: uploaded.width, height: uploaded.height });
          toast.success("Image uploaded successfully");
        } else {
          toast.error("Failed to upload image");
        }
      } catch (err) {
        toast.error("Error uploading image");
      }
    };
  };

  useEffect(() => {
    if (mode === "edit" && activeSection === "email" && editContent.current) {
      const quill = editContent.current.getEditor();
      quill.getModule("toolbar").addHandler("image", imageHandler);
    }
    // eslint-disable-next-line
  }, [mode, activeSection]);

  const cleanupUnusedImages = (htmlContent) => {
    const imgRegex = /<img[^>]+src="([^">]+)"/g;
    const used = [];
    let m;
    while ((m = imgRegex.exec(htmlContent))) used.push(m[1]);
    uploadedImages
      .filter((url) => !used.includes(url))
      .forEach((url) =>
        dispatch(marketingAction.deleteEmailBodyImage(url)).catch(() => {})
      );
    setUploadedImages(used);
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }
    if (!file.type.match("image.*")) {
      toast.error("Please select an image file");
      return;
    }
    setUploadingImage(true);
    dispatch(marketingAction.uploadReminderImage(file))
      .then((response) => {
        if (response?.data?.statusCode === 1) {
          setSalonImage(response.data.responseData.imageUrl);
          toast.success("Image uploaded successfully");
        } else {
          toast.error("Failed to upload image");
        }
      })
      .catch(() => toast.error("Error uploading image"))
      .finally(() => setUploadingImage(false));
  };

  // ── Merge-tag insertion ──
  const insertIntoQuill = (ref, text, stateSetter) => {
    if (!ref.current) return;
    const editor = ref.current.getEditor();
    const range = editor.getSelection();
    if (range) {
      editor.insertText(range.index, text, "user");
      editor.setSelection(range.index + text.length, 0);
    } else {
      const len = editor.getLength();
      editor.insertText(len - 1, text, "user");
      editor.setSelection(len - 1 + text.length, 0);
    }
    editor.focus();
    stateSetter(editor.root.innerHTML);
  };
  const addToBody = (text) => insertIntoQuill(editContent, text, (html) => setField("emailText", html));
  const addToFooter = (text) => insertIntoQuill(footerTextRef, text, (html) => setFooterText(html));
  const addToSubject = (text) => {
    const cur = form.emailSubject || "";
    setField("emailSubject", cur ? `${cur} ${text}` : text);
    if (emailSubjectRef.current) emailSubjectRef.current.focus();
  };
  const addToSms = (text) => {
    const prev = form.smsText || "";
    if ((prev + " " + text + " ").length <= 140) setField("smsText", prev + " " + text + " ");
    if (smsTextRef.current) smsTextRef.current.focus();
  };

  // ── Previews (work from saved `data` in view mode, edited `form` in edit mode) ──
  const openEmailPreview = (src) => {
    const o =
      src === "edit"
        ? { emailText: form.emailText, title: form.title, headerImage: salonImage, footerText }
        : {
            emailText: data?.emailText || data?.automationText,
            title: data?.title,
            headerImage: data?.headerImage,
            footerText: data?.footerText,
          };
    setEmailPreviewHtml(
      generateCustomEmailHTML({
        previewText: o.emailText,
        title: o.title || "Custom Reminder",
        headerImage: o.headerImage,
        footerText: o.footerText,
      })
    );
  };

  // ── Validation ── (description is intentionally NOT required — the backend
  // accepts an empty description; it's an optional field.)
  const validate = (d) => {
    const next = {};
    if (!d.title) next.title = "Field is required.";
    if (!d.triggerType || typeof d.triggerType.value !== "number") next.triggerType = "Field is required.";
    if (!/^[1-9][0-9]*$/.test(String(d.reminderTime))) next.reminderTime = "Enter a valid time.";
    if (!d.reminderTimeType || typeof d.reminderTimeType.value !== "number") next.reminderTimeType = "Field is required.";
    if (activeTab === "email" && emailFormat !== "blocks" && !d.emailText) next.emailText = "Field is required.";
    if (activeTab === "sms") {
      if (!d.smsText) next.smsText = "Field is required.";
      else if (d.smsText.length > 140) next.smsText = "SMS text must be 140 characters or less";
    }
    setErrors(next);
    return next;
  };

  // Make a failed save visible: jump to the section holding the first error and
  // toast it, so a missing field on another tab is never a silent dead button.
  const surfaceValidationErrors = (errs) => {
    const keys = Object.keys(errs).filter((k) => errs[k]);
    if (!keys.length) return;
    const detailKey = ["title", "triggerType", "reminderTime", "reminderTimeType"].find((f) => errs[f]);
    if (detailKey) { goSection("details"); toast.error(errs[detailKey]); return; }
    if (errs.emailText) { goSection("email"); toast.error(errs.emailText); return; }
    if (errs.smsText) { goSection("sms"); toast.error(errs.smsText); return; }
    toast.error("Please fix the highlighted fields before saving");
  };

  const save = () => {
    const d = {
      ...form,
      statusSMSreminder: form.statusSMSreminder === 1 ? 1 : 0,
      statusEmailReminder: form.statusEmailReminder === 1 ? 1 : 0,
      smsText: form.smsText || "",
      emailText: form.emailText || "",
      emailSubject: form.emailSubject || "",
    };
    const errs = validate(d);
    if (Object.keys(errs).some((k) => errs[k])) {
      surfaceValidationErrors(errs);
      return;
    }
    if (d.emailText) cleanupUnusedImages(d.emailText + (footerText || ""));

    // A themed design goes as the finished email (the same document the
    // campaign creator sends); the server prefers it over emailText. Anything
    // else sends an empty emailHtml, which clears a stale one.
    const subjectForHtml = d.emailSubject || "";
    let emailHtml = "";
    if (emailFormat === "blocks" && isThemedEmail(emailBlocks)) {
      try {
        emailHtml = renderEmail(resolveLocalLinks(emailBlocks, salonLinks(user?.userInfo?.hairdresser)), { subject: subjectForHtml });
      } catch (e) {
        emailHtml = "";
      }
    }

    const reminderObj = {
      reminderTime: +d.reminderTime,
      reminderTimeType: d.reminderTimeType?.value,
      title: d.title,
      description: d.description,
      smsText: d.smsText,
      emailText: emailFormat === "blocks" ? (blocksToFallbackHtml(emailBlocks) || constrainImages(d.emailText)) : constrainImages(d.emailText),
      emailFormat,
      emailBlocks: emailFormat === "blocks" ? emailBlocks : [],
      emailHtml,
      emailSubject: d.emailSubject || `Reminder from ${user?.userInfo?.hairdresser?.salon?.name || "your salon"}`,
      statusSMSreminder: d.statusSMSreminder,
      statusEmailReminder: d.statusEmailReminder,
      triggerType: d.triggerType?.value,
      headerImage: salonImage || "",
      footerText: constrainImages(footerText || ""),
    };

    setSaving(true);
    const onDone = (res) => {
      setSaving(false);
      if (res?.data?.statusCode === 1) {
        toast.success(res?.data?.responseData?.message);
        if (onSaved) onSaved();
      }
    };
    const fail = () => setSaving(false);

    if (isCreate) {
      const payload = {
        reminder: reminderObj,
        salonId: salonId || localStorage.getItem("salonId"),
        type: 5,
      };
      dispatch(marketingAction.addReminder(payload)).then(onDone).catch(fail);
    } else {
      const payload = {
        reminders: [{ _id: data?._id, ...reminderObj }],
        type: 5,
        salonId,
      };
      dispatch(marketingAction.updateSetting(payload)).then(onDone).catch(fail);
    }
  };

  const remove = () => {
    setSaving(true);
    dispatch(marketingAction.removeReminder({ reminderId: data?._id, type: 5, salonId }))
      .then((res) => {
        setSaving(false);
        if (res?.data?.statusCode === 1) {
          toast.success(res?.data?.responseData?.message);
          if (onSaved) onSaved();
        }
      })
      .catch(() => setSaving(false));
  };

  const NAV = [
    { id: "details", label: "Details" },
    { id: "email", label: "Email", on: form.statusEmailReminder === 1 },
    { id: "sms", label: "SMS", on: form.statusSMSreminder === 1 },
  ];

  const triggerLabel = (triggerTypeOptions.find((t) => t?.value === data?.triggerType) || {}).label || "Not set";
  const timingLabel =
    data?.reminderTime != null && data?.reminderTimeType != null
      ? `${data.reminderTime} ${marketingAction.reminderTimeType(data.reminderTimeType)}`
      : "Not set";
  const emailOn = data?.statusEmailReminder === 1;
  const smsOn = data?.statusSMSreminder === 1;

  return (
    <>
    <SlidePanel onClose={onClose} width={mode === "edit" ? 860 : 460} className="crp-sheet">
      {mode === "view" && (
        <>
          <div className="crp-view-header">
            <div className="crp-avatar">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <div className="crp-head-info">
              <h2 className="crp-head-name">{data?.title || "Automation"}</h2>
            </div>
            <div className="crp-head-actions">
              <div style={{ position: "relative" }}>
                <button type="button" className="crp-icon-btn" onClick={() => setShowMenu((s) => !s)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="12" cy="19" r="1.6" /></svg>
                </button>
                {showMenu && (
                  <>
                    <div style={{ position: "fixed", inset: 0, zIndex: 9 }} onClick={() => setShowMenu(false)} />
                    <div className="crp-menu">
                      <button type="button" className="crp-menu-item" onClick={() => { setShowMenu(false); setMode("edit"); }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                        Edit automation
                      </button>
                      <button type="button" className="crp-menu-item crp-menu-item--danger" onClick={() => { setShowMenu(false); setConfirmDelete(true); }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                        Delete automation
                      </button>
                    </div>
                  </>
                )}
              </div>
              <button type="button" className="crp-icon-btn" onClick={onClose}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
              </button>
            </div>
          </div>

          <div className="crp-summary">
            <span className="crp-summary-item"><span className="crp-summary-value">{triggerLabel}</span>trigger</span>
            <span className="crp-summary-item"><span className="crp-summary-value">{timingLabel}</span>before sending</span>
          </div>

          <div className="crp-view-body">
            <div className="crp-info-group">
              <div className="crp-info-group-title">Channels</div>
              <div className="crp-info-row">
                <span className="crp-info-label">Email</span>
                <span className={`crp-badge ${emailOn ? "crp-badge--on" : "crp-badge--off"}`}>{emailOn ? "On" : "Off"}</span>
              </div>
              <div className="crp-info-row">
                <span className="crp-info-label">SMS</span>
                <span className={`crp-badge ${smsOn ? "crp-badge--on" : "crp-badge--off"}`}>{smsOn ? "On" : "Off"}</span>
              </div>
            </div>

            {data?.description ? (
              <div className="crp-info-group">
                <div className="crp-info-group-title">Description</div>
                <p className="crp-text-block">{data.description}</p>
              </div>
            ) : null}

            <div className="crp-info-group">
              <div className="crp-info-group-title">Email</div>
              <div className="crp-info-row">
                <span className="crp-info-label">Subject</span>
                <span className="crp-info-value">{data?.emailSubject || "Not set"}</span>
              </div>
              <button type="button" className="crp-preview-btn" onClick={() => openEmailPreview("view")}>Preview email</button>
            </div>

            <div className="crp-info-group">
              <div className="crp-info-group-title">SMS message</div>
              <p className="crp-text-block">{data?.smsText || "No SMS content yet."}</p>
              <button type="button" className="crp-preview-btn" onClick={() => setSmsInlineOpen((o) => !o)}>
                {smsInlineOpen ? "Hide preview" : "Preview SMS"}
              </button>
              {smsInlineOpen && (
                <div className="mep-preview">
                  <SmsPreview text={data?.smsText || defaultReminderText} />
                </div>
              )}
            </div>
          </div>

          {/* Editing is the common reason to open this, so it gets a pinned
              CTA rather than living only behind the overflow menu. */}
          <div className="crp-edit-footer">
            <button type="button" className="sp-btn-primary" style={{ width: "100%" }} onClick={() => setMode("edit")}>
              Edit automation
            </button>
          </div>
        </>
      )}

      {mode === "edit" && (
        <>
          <div className="crp-header">
            <h2 className="crp-title">{isCreate ? "Create Custom Automation" : "Edit " + (data?.title || "Automation")}</h2>
            <button type="button" className="crp-icon-btn" onClick={onClose}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
            </button>
          </div>

      <div className="crp-steps">
        {NAV.map((n) => (
          <button
            key={n.id}
            type="button"
            className={`crp-step${activeSection === n.id ? " crp-step--active" : ""}`}
            onClick={() => goSection(n.id)}
          >
            {n.label}
          </button>
        ))}
      </div>

      <div className="crp-content">
          {activeSection === "details" && (
            <>
              <h3 className="crp-section-title">Details</h3>
              <p className="crp-section-sub">Name your automation and set when it sends.</p>

              <div className="crp-field">
                <label className="sp-label">Automation name</label>
                <input className="sp-input" type="text" value={form.title} onChange={(e) => setField("title", e.target.value)} />
                {errors.title ? <div className="sp-error">{errors.title}</div> : null}
              </div>
              <div className="crp-field">
                <label className="sp-label">Automation description <span style={{ color: "#9ca3af", fontWeight: 400 }}>(optional)</span></label>
                <input className="sp-input" type="text" value={form.description} onChange={(e) => setField("description", e.target.value)} />
              </div>
              <div className="crp-field">
                <label className="sp-label">Trigger</label>
                <Select
                  value={form.triggerType}
                  options={triggerTypeOptions}
                  onChange={(e) => setField("triggerType", e)}
                  {...marketingSelectProps}
                />
                {errors.triggerType ? <div className="sp-error">{errors.triggerType}</div> : null}
              </div>
              <div className="mkt-time-row">
                <div className="mkt-time-trigger">
                  <label className="sp-label">Trigger Time</label>
                  <input
                    className="sp-input"
                    type="number"
                    style={{ textAlign: "center" }}
                    value={form.reminderTime}
                    onChange={(e) => setField("reminderTime", e.target.value)}
                  />
                  {errors.reminderTime ? <div className="sp-error">{errors.reminderTime}</div> : null}
                </div>
                <div className="mkt-time-frame">
                  <label className="sp-label">Timeframe</label>
                  <Select
                    value={form.reminderTimeType}
                    options={reminderTimeTypeOption}
                    onChange={(e) => setField("reminderTimeType", e)}
                    {...marketingSelectProps}
                  />
                  {errors.reminderTimeType ? <div className="sp-error">{errors.reminderTimeType}</div> : null}
                </div>
              </div>
            </>
          )}

          {activeSection === "email" && (
            <>
              <h3 className="crp-section-title">Email</h3>
              <p className="crp-section-sub">Compose the email clients receive.</p>

              <div className="crp-channel-bar">
                <div className="crp-channel-bar-text">
                  <strong>Send email</strong>
                  <span>Free</span>
                </div>
                <ChannelToggle
                  checked={form.statusEmailReminder === 1}
                  onChange={(c) => setField("statusEmailReminder", c ? 1 : 0)}
                />
              </div>

              <div className="crp-field">
                <label className="sp-label">Email Subject Line</label>
                <div className="editable-border">
                  <MergeTags onInsert={addToSubject} />
                  <textarea
                    ref={emailSubjectRef}
                    value={form.emailSubject || ""}
                    onChange={(e) => e.target.value.length <= 140 && setField("emailSubject", e.target.value)}
                    className="newEditableContent"
                    style={{ minHeight: 30 }}
                    placeholder="Enter email subject line..."
                    maxLength={140}
                  />
                </div>
                {form.emailSubject ? <small className="text-muted">{form.emailSubject.length}/140 characters</small> : null}
              </div>

              {emailFormat === "blocks" ? (
                <div className="crp-field">
                  <label className="sp-label">Email Design</label>
                  {emailBlocks.length ? (
                    <button
                      type="button"
                      onClick={() => setShowDesigner(true)}
                      style={{
                        display: "block", width: "100%", padding: 0, border: "1px solid #ECECF0",
                        borderRadius: 14, overflow: "hidden", background: "#fff", cursor: "pointer", textAlign: "left"
                      }}
                      title="Edit design"
                    >
                      <div style={{ height: 300, overflow: "hidden", pointerEvents: "none" }}>
                        <EmailBlocksPreview
                          blocks={emailBlocks}
                          clipHeight={300}
                          fallback={() => (
                            <div style={{ transform: "scale(0.55)", transformOrigin: "top left", width: "182%" }}>
                              {emailBlocks.slice(0, 5).map((b, i) => (
                                <BlockRenderer key={b.id || i} block={b} />
                              ))}
                            </div>
                          )}
                        />
                      </div>
                      <div style={{ padding: "10px 14px", borderTop: "1px solid #F2F2F7", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 12.5, fontWeight: 600, color: "#4B4B52" }}>{emailBlocks.length} blocks</span>
                        <span style={{ fontSize: 12.5, fontWeight: 700, color: "#007EA0" }}>Edit design</span>
                      </div>
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowDesigner(true)}
                      style={{
                        width: "100%", padding: "36px 20px", border: "1.5px dashed #D6D6DC", borderRadius: 14,
                        background: "#fff", cursor: "pointer", textAlign: "center", fontFamily: "inherit"
                      }}
                    >
                      <span style={{ display: "block", fontSize: 14, fontWeight: 600, color: "#29282D", marginBottom: 4 }}>Create your email design</span>
                      <span style={{ display: "block", fontSize: 12.5, color: "#8E8E93" }}>Start from a template or a blank canvas.</span>
                    </button>
                  )}
                  <p className="crp-section-sub" style={{ margin: "10px 0 0" }}>
                    A simple text version is generated automatically so every email app can read it.
                  </p>
                  <button
                    type="button"
                    onClick={() => setEmailFormat("text")}
                    style={{ border: "none", background: "none", padding: "8px 0 0", fontSize: 12.5, fontWeight: 600, color: "#8E8E93", cursor: "pointer", fontFamily: "inherit", textDecoration: "underline" }}
                  >
                    Switch to a simple text email
                  </button>
                </div>
              ) : (
                <>
                  <div className="crp-field">
                    <button
                      type="button"
                      onClick={() => setShowDesigner(true)}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
                        width: "100%", padding: "13px 16px", border: "1px solid rgba(0,126,160,0.3)",
                        borderRadius: 12, background: "rgba(0,126,160,0.05)", cursor: "pointer",
                        fontFamily: "inherit", textAlign: "left"
                      }}
                    >
                      <span>
                        <span style={{ display: "block", fontSize: 13.5, fontWeight: 700, color: "#007EA0", marginBottom: 2 }}>Design a beautiful email instead</span>
                        <span style={{ display: "block", fontSize: 12.5, color: "#4B4B52" }}>Use the email designer with templates, photos and your branding.</span>
                      </span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#007EA0" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M9 18l6-6-6-6"/></svg>
                    </button>
                  </div>
              <div className="crp-field">
                <label className="sp-label">Email Header Image</label>
                {salonImage ? (
                  <img src={salonImage} alt="Header" className="img-fluid mb-2" style={{ maxHeight: 80 }} />
                ) : (
                  <p className="crp-section-sub" style={{ margin: "0 0 8px" }}>
                    No image yet. Upload a custom header if you'd like one.
                  </p>
                )}
                <div>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => fileInputRef.current.click()}
                    disabled={uploadingImage}
                  >
                    {uploadingImage ? "Uploading..." : "Upload Custom Image"}
                  </button>
                  <input type="file" ref={fileInputRef} style={{ display: "none" }} accept="image/*" onChange={handleImageUpload} />
                </div>
              </div>

              <div className="crp-field">
                <label className="sp-label">Email Body Text</label>
                <MergeTags onInsert={addToBody} />
                <div className="ql-editor-wrapper" style={{ direction: "ltr" }}>
                  <ReactQuill
                    ref={editContent}
                    defaultValue={form.emailText || ""}
                    onChange={(content) => setField("emailText", content)}
                    modules={quillModules}
                    theme="snow"
                    placeholder="Enter email body text"
                  />
                </div>
                {errors.emailText ? <div className="sp-error">{errors.emailText}</div> : null}
              </div>

              <div className="crp-field">
                <label className="sp-label">Email Footer</label>
                <MergeTags onInsert={addToFooter} />
                <div className="ql-editor-wrapper" style={{ direction: "ltr" }}>
                  <ReactQuill
                    ref={footerTextRef}
                    defaultValue={footerText || ""}
                    onChange={(content) => setFooterText(content)}
                    modules={quillModules}
                    theme="snow"
                    placeholder="Enter footer text"
                  />
                </div>
              </div>

              <button type="button" className="crp-preview-btn" onClick={() => openEmailPreview("edit")}>
                Preview Email
              </button>
                </>
              )}
            </>
          )}

          {activeSection === "sms" && (
            <>
              <h3 className="crp-section-title">SMS</h3>
              <p className="crp-section-sub">Compose the text message clients receive.</p>

              <div className="crp-channel-bar">
                <div className="crp-channel-bar-text">
                  <strong>Send SMS</strong>
                  <span>£0.04 / message</span>
                </div>
                <ChannelToggle
                  checked={form.statusSMSreminder === 1}
                  onChange={(c) => setField("statusSMSreminder", c ? 1 : 0)}
                />
              </div>

              <div className="crp-field">
                <label className="sp-label">SMS Text</label>
                <div className="editable-border">
                  <MergeTags onInsert={addToSms} />
                  <textarea
                    ref={smsTextRef}
                    value={form.smsText || ""}
                    onChange={(e) => e.target.value.length <= 140 && setField("smsText", e.target.value)}
                    className="newEditableContent"
                    style={{ minHeight: 100 }}
                    placeholder={defaultReminderText}
                  />
                </div>
                {form.smsText ? <small className="text-muted">{form.smsText.length}/140 characters</small> : null}
                {errors.smsText ? <div className="sp-error">{errors.smsText}</div> : null}
              </div>

              <button
                type="button"
                className="crp-preview-btn"
                onClick={() => setSmsInlineOpen((o) => !o)}
              >
                {smsInlineOpen ? "Hide preview" : "Preview SMS"}
              </button>
              {smsInlineOpen && (
                <div className="mep-preview">
                  <SmsPreview text={form.smsText || defaultReminderText} />
                </div>
              )}
            </>
          )}
        </div>

          <div className="crp-edit-footer">
            <button type="button" className="sp-btn-secondary" onClick={() => (isCreate ? onClose() : setMode("view"))}>
              Cancel
            </button>
            <button type="button" className="sp-btn-primary" onClick={save} disabled={saving}>
              {saving ? "Saving..." : isCreate ? "Create Automation" : "Save Changes"}
            </button>
          </div>
        </>
      )}

      {confirmDelete &&
        ReactDOM.createPortal(
          <div className="sp-confirm-overlay" style={{ zIndex: 100020 }} onClick={() => setConfirmDelete(false)}>
            <div className="sp-confirm-card" onClick={(e) => e.stopPropagation()}>
              <div className="sp-confirm-icon sp-confirm-icon--danger">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
              </div>
              <h3 className="sp-confirm-title">Delete automation?</h3>
              <p className="sp-confirm-text">This permanently removes "{data?.title}". This can't be undone.</p>
              <div className="sp-confirm-actions">
                <button type="button" className="sp-btn-secondary" onClick={() => setConfirmDelete(false)}>
                  Cancel
                </button>
                <button type="button" className="sp-btn-primary" style={{ background: "#dc2626" }} onClick={() => { setConfirmDelete(false); remove(); }} disabled={saving}>
                  Delete
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {emailPreviewHtml && (
        <CustomEmailPreviewPanel html={emailPreviewHtml} onClose={() => setEmailPreviewHtml(null)} />
      )}
    </SlidePanel>
    {showDesigner && ReactDOM.createPortal(
      <div style={{ position: "fixed", inset: 0, zIndex: 100015 }}>
        <CampaignComposer
          mode="design"
          initialBlocks={emailBlocks}
          initialSubject={form.emailSubject}
          onBack={() => setShowDesigner(false)}
          onSaveDesign={({ blocks, subject }) => {
            setEmailBlocks(blocks);
            setEmailFormat("blocks");
            if (subject) setField("emailSubject", subject);
            setShowDesigner(false);
          }}
        />
      </div>,
      document.body
    )}
    </>
  );
}

export default CustomReminderPanel;
